import React, { useEffect, useId, useRef, useState } from 'react'
import moment from 'moment'
import { Popover } from 'reactstrap'

import Pager from './Pager'
import usePagination, { DEFAULT_PAGE_SIZE } from './usePagination'
import { heatLevel, slotForColor } from '../activityReport'

// Joined with a separator that never appears in a period key (always a plain
// ISO date or 'YYYY-MM' string), so two different rows can never collide onto
// the same open-popover key just because one row name happens to be a prefix
// of another's.
const cellKey = (rowValue, day) => `${rowValue}||${day}`

/**
 * Rows (users, or boards) x time-bucket intensity grid.
 *
 * Magnitude is the job here, so this is a sequential single-hue ramp rather than
 * categorical colour. Cells carry a `viz-heat--n` class instead of a computed
 * background, which keeps colour in the stylesheet and gives the ramp exactly
 * seven discrete steps.
 *
 * Counts live in the cell title rather than printed in the cell: at a week's
 * width the cells are too small for legible numerals, and the darker steps would
 * fail contrast against dark text. Clicking a cell (or activating it from the
 * keyboard) opens a small popover with the same total broken out by action --
 * see `byAction` in `buildHeatmap` (activityReport.js) for how that split is
 * computed (the same `totalsByMulti` / `actionMeta` machinery as the cell's own
 * count, just keyed one segment finer, so the two can never disagree) -- so the
 * numeral nobody can fit in a 26px square is one click away instead of lost.
 *
 * Only one popover is ever open at a time (`open`, not one-flag-per-cell) and
 * only one is ever mounted, rendered once after the table (and its pager)
 * rather than per-cell -- it targets whichever cell's button is currently
 * open via reactstrap's `Popover` (`target` accepts a DOM element, so no
 * per-cell DOM id is needed across a whole paginated grid) with
 * `container="body"`, so it escapes `.viz-heatmap-scroll`'s own
 * `overflow: auto; max-height` box instead of being clipped by it -- the
 * clipping this replaces was worst on a short (one- or few-row) heatmap and
 * the last handful of rows of any page. `trigger="manual"` and `fade={false}`
 * hand every bit of open/close, click-outside, Escape and focus behaviour to
 * this component instead of reactstrap's own (which keeps content mounted
 * and click-through for ~150ms after close while it plays an exit
 * transition, and reads its trigger by a `target` id that only ever names
 * one element).
 *
 * Closed by: re-activating the same cell, a click outside the popover,
 * Escape (which also returns focus to the trigger), the page changing
 * (`usePagination`'s `page`, which reshuffles which rows are even on
 * screen), or the open row dropping off the *visible page* of `grid` (e.g. a
 * filter narrowing the report data away from it, or a re-rank moving it to
 * another page) -- a stale trigger would otherwise keep the document
 * listeners below attached to a button that no longer exists. Either of
 * those last two is a close the component itself decides to make, not the
 * user, so if focus was inside the popover when it fires, it's moved to the
 * row's trigger (if still mounted) or the heatmap's own scroll container
 * (if not) rather than left to fall onto `<body>` when the portaled dialog
 * unmounts -- see `closeStrandedPopover`. Opening moves focus into the
 * popover's own dialog container (`tabIndex={-1}`): it is portaled to
 * `<body>`, so without that, focus would stay on the cell button while the
 * popover renders somewhere else in the DOM entirely.
 *
 * Columns are `periods` (from `buildHeatmap`'s granularity-aware `{ key, label,
 * start, end }` buckets) when the caller passes them, falling back to plain
 * `MMM D` day labels built from `days` for a caller (or test) that only ever
 * knew about day columns. A single-day bucket (`start === end`, true for every
 * day-granularity column and any week/month bucket clamped to one day at the
 * edge of the range) keeps today's two-line `D` / `MMM` header; a genuine
 * multi-day bucket renders its own label on one line instead.
 */
const ActivityHeatmap = ({
    days,
    periods,
    grid,
    peak,
    pageSize = DEFAULT_PAGE_SIZE,
    rowKey = 'userName',
    rowHeader = 'User',
    unit = 'users',
    onInvestigate,
}) => {
    // Rows are paged so the card stays a bounded height; the day columns still
    // scroll horizontally, which keeps the user column pinned beside them.
    const { pageItems, page, pageCount, setPage, start, total } = usePagination(grid, pageSize)

    const columns = periods || days.map((day) => ({ key: day, label: moment(day).format('MMM D') }))
    const periodByKey = {}
    columns.forEach((period) => {
        periodByKey[period.key] = period
    })

    // The currently open popover's own display data (`null` when closed) --
    // captured whole at click time rather than re-derived from `openKey` on
    // every render, since the single popover below is rendered once, outside
    // the row loop that has the rest of this data to hand.
    const [open, setOpen] = useState(null)
    const triggerRefs = useRef({})
    const popoverContentRef = useRef(null)
    // Fallback focus target for a non-user-initiated close (page or grid
    // effect below) whose row's trigger button is no longer mounted --
    // `tabIndex={-1}` makes it focusable by script without adding it to the
    // Tab order.
    const scrollContainerRef = useRef(null)
    const popoverId = useId()
    const headingId = useId()

    // Shared by every close that isn't driven directly by the user's own
    // click/keypress (the page and grid effects below) -- a click on the
    // trigger, a click outside, or Escape all already manage focus
    // themselves (see the effects further down). Those two can instead fire
    // while focus is still inside the popover's own `<div role="dialog">`,
    // and since that div is portaled to `<body>` (see `container="body"`
    // below), simply unmounting it would drop focus onto `<body>` itself --
    // invisible to a screen reader and unable to show a keyboard user where
    // they landed. Move focus to the row's own trigger button first, since
    // it's usually still the most useful place to land; fall back to the
    // heatmap's own scroll container for the case -- e.g. the open row just
    // left the grid, or the visible page -- where the trigger isn't there to
    // land on either. Never touches focus that's already elsewhere (a Pager
    // click, for instance, already holds its own focus).
    const closeStrandedPopover = () => {
        setOpen((current) => {
            if (!current) return current
            if (popoverContentRef.current?.contains(document.activeElement)) {
                const trigger = triggerRefs.current[current.key]
                if (trigger) trigger.focus()
                else scrollContainerRef.current?.focus({ preventScroll: true })
            }
            return null
        })
    }

    // A page change reshuffles which rows are even on screen -- close
    // whatever was open rather than leave it pointing at a cell that just
    // scrolled out of the grid.
    useEffect(() => {
        closeStrandedPopover()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    // `grid` itself can change under an open popover too -- a filter, a
    // re-fetch, or simply a re-rank that narrows the report data or moves
    // the open row onto another page can drop the very row/cell the popover
    // is anchored to off the *visible* page. `pageItems`, not the whole
    // `grid`, is what's actually rendered -- checking against the whole grid
    // would leave `open` (and the trigger the popover targets) pointing at a
    // row that still exists in the data but no longer has a mounted button,
    // so the popover would hide with nothing to reopen it, then silently
    // reappear, pointed at nothing new, if the row's old page is revisited.
    // Close it rather than leave that stale state around.
    useEffect(() => {
        if (!open) return
        const stillOnPage = pageItems.some((row) =>
            row.cells.some((cell) => cellKey(row[rowKey], cell.day) === open.key)
        )
        if (!stillOnPage) closeStrandedPopover()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grid])

    useEffect(() => {
        if (!open) return undefined

        const onDocumentClick = (event) => {
            // The popover is portaled to `document.body` (see the `Popover`
            // below), not nested under the cell -- `contains` still works
            // for it exactly like it does for the trigger button, since a
            // portalled node is still part of the same document.
            if (popoverContentRef.current?.contains(event.target)) return
            if (triggerRefs.current[open.key]?.contains(event.target)) return
            setOpen(null)
        }
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return
            const trigger = triggerRefs.current[open.key]
            setOpen(null)
            trigger?.focus()
        }

        document.addEventListener('mousedown', onDocumentClick)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onDocumentClick)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    // Moves focus into the popover once it is actually open. Needed because
    // the popover is portaled to `<body>` (see `container="body"` below), so
    // it is nowhere near the cell button in tab order -- without this, focus
    // would simply stay on the button while the popover appears elsewhere in
    // the DOM. Targets the dialog container itself (`tabIndex={-1}` below)
    // rather than a control inside it, since "View in Investigate" is not
    // always present. Escape and click-outside both return focus to the
    // trigger (see the effect above), so focus never gets stranded on
    // `<body>` either way.
    //
    // `preventScroll` matters here: the dialog is portaled to `<body>` and is
    // focused before Popper has moved it next to the cell, so a plain
    // `.focus()` makes the browser scroll the page to wherever the unplaced
    // popover sits -- the top of the page -- and the user has to scroll back
    // down to find it.
    useEffect(() => {
        if (open) popoverContentRef.current?.focus({ preventScroll: true })
        // Deliberately keyed on `open?.key` alone, not `open` itself -- the
        // object is rebuilt on every click (including a same-cell toggle,
        // which closes it), and re-focusing on every one of those renders
        // would fight a user tabbing inside the popover's own content.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open?.key])

    const toggleCell = (row, cell, period, periodText) => {
        const key = cellKey(row[rowKey], cell.day)
        setOpen((current) =>
            current?.key === key
                ? null
                : {
                      key,
                      rowName: row[rowKey],
                      // Carried through to `onInvestigate` untouched -- only
                      // meaningful (non-undefined) for a `userName`-keyed
                      // heatmap row whose rows all share one `userId`; see
                      // `buildHeatmap`'s own `userId` field and
                      // `InvestigateTab`'s matching logic for the one case
                      // (two different people sharing a display name) this
                      // still can't disambiguate.
                      userId: row.userId,
                      periodText,
                      period,
                      count: cell.count,
                      byAction: cell.byAction || [],
                  }
        )
    }

    const registerTrigger = (key) => (node) => {
        if (node) triggerRefs.current[key] = node
        else delete triggerRefs.current[key]
    }

    const openTarget = open ? triggerRefs.current[open.key] : null
    const openCountLabel = open ? `${open.count.toLocaleString()} action${open.count === 1 ? '' : 's'}` : ''

    return (
        <>
            <div className="viz-heatmap-scroll" ref={scrollContainerRef} tabIndex={-1}>
                <table className="viz-heatmap">
                    <thead>
                        <tr>
                            <th className="viz-heatmap-corner">{rowHeader}</th>
                            {columns.map((period) => {
                                const isBucket = period.start !== undefined && period.start !== period.end
                                const title = isBucket
                                    ? `${moment(period.start).format('dddd, MMM D YYYY')} – ${moment(period.end).format('dddd, MMM D YYYY')}`
                                    : moment(period.key).format('dddd, MMM D YYYY')
                                return (
                                    <th key={period.key} className="viz-heatmap-day" title={title}>
                                        {isBucket ? (
                                            <span className="viz-heatmap-day-label">{period.label}</span>
                                        ) : (
                                            <>
                                                <span className="viz-heatmap-day-label">{moment(period.key).format('D')}</span>
                                                <span className="viz-heatmap-day-month">{moment(period.key).format('MMM')}</span>
                                            </>
                                        )}
                                    </th>
                                )
                            })}
                            <th className="viz-heatmap-total">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((row) => (
                            <tr key={row[rowKey]}>
                                <th className="viz-heatmap-user" title={row[rowKey]}>{row[rowKey]}</th>
                                {row.cells.map((cell) => {
                                    const period = periodByKey[cell.day]
                                    const cellLabel = period?.label || moment(cell.day).format('MMM D')
                                    const key = cellKey(row[rowKey], cell.day)
                                    const isOpen = open?.key === key
                                    const countLabel = `${cell.count.toLocaleString()} action${cell.count === 1 ? '' : 's'}`
                                    return (
                                        <td key={cell.day} className="viz-heatmap-cell">
                                            <button
                                                type="button"
                                                ref={registerTrigger(key)}
                                                className={`viz-heat viz-heat--${heatLevel(cell.count, peak)}`}
                                                title={`${row[rowKey]} — ${cellLabel}: ${countLabel}`}
                                                aria-label={`${row[rowKey]}, ${cellLabel}: ${countLabel}`}
                                                aria-haspopup="dialog"
                                                aria-expanded={isOpen}
                                                aria-controls={isOpen ? popoverId : undefined}
                                                onClick={() => toggleCell(row, cell, period, cellLabel)}
                                            >
                                                <span className="viz-visually-hidden">{cell.count.toLocaleString()}</span>
                                            </button>
                                        </td>
                                    )
                                })}
                                <td className="viz-heatmap-total">{row.total.toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="viz-heatmap-legend">
                    <span className="viz-heatmap-legend-label">Less</span>
                    {[0, 1, 2, 3, 4, 5, 6].map((level) => (
                        <span key={level} className={`viz-heat-key viz-heat--${level}`} />
                    ))}
                    <span className="viz-heatmap-legend-label">More</span>
                    <span className="viz-heatmap-legend-peak">Peak {peak.toLocaleString()} in a day</span>
                </div>
            </div>
            <Pager
                page={page}
                pageCount={pageCount}
                setPage={setPage}
                start={start}
                count={pageItems.length}
                total={total}
                unit={unit}
            />
            {open && openTarget && (
                <Popover
                    isOpen
                    target={openTarget}
                    container="body"
                    placement="bottom"
                    trigger="manual"
                    fade={false}
                    hideArrow
                    popperClassName="viz-heat-popover-layer"
                >
                    <div
                        ref={popoverContentRef}
                        id={popoverId}
                        className="viz-heat-popover"
                        role="dialog"
                        aria-modal="false"
                        aria-labelledby={headingId}
                        tabIndex={-1}
                    >
                        <div className="viz-heat-popover-header" id={headingId}>
                            {open.rowName} · {open.periodText} · {openCountLabel}
                        </div>
                        {open.byAction.length > 0 ? (
                            <ul className="viz-heat-popover-breakdown">
                                {open.byAction.map((entry) => (
                                    <li key={entry.action} className="viz-heat-popover-breakdown-row">
                                        <span
                                            className={`viz-heat-popover-swatch viz-swatch viz-swatch--${slotForColor(entry.color)}`}
                                            aria-hidden="true"
                                        />
                                        <span className="viz-heat-popover-breakdown-label">{entry.label}</span>
                                        <span className="viz-heat-popover-breakdown-count">{entry.count.toLocaleString()}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="viz-heat-popover-empty">No activity recorded</p>
                        )}
                        {/* Only for a heatmap keyed by person -- ReportBody never hands a
                            scope (board/inbox) heatmap this callback, since "view this
                            board's activity as one actor" doesn't mean anything. */}
                        {onInvestigate && (
                            <button
                                type="button"
                                className="viz-heat-popover-investigate"
                                onClick={() => onInvestigate(open.rowName, open.period, open.userId)}
                            >
                                View in Investigate <span aria-hidden="true">→</span>
                            </button>
                        )}
                    </div>
                </Popover>
            )}
        </>
    )
}

export default ActivityHeatmap
